import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, LineChart, Line, PieChart, Pie, Cell
} from 'recharts';
import { 
  getStrategyConfig, 
  STRATEGY_TYPES, 
  DEFAULT_GROWTH_RATE, 
  SELLING_COSTS 
} from '../config/strategyConfig';

/** Footer line: landlord strategies use cash-on-cash / cash to complete; project strategies use Project ROI / Capital required. */
function StrategyRoiCapitalFooter({ strategyType, strategyData }) {
  if (!strategyData) return null;
  const roiNum =
    strategyData.estimated_roi_percent != null && strategyData.estimated_roi_percent !== ''
      ? Number(strategyData.estimated_roi_percent)
      : NaN;
  const capNum =
    strategyData.initial_capital_required != null && strategyData.initial_capital_required !== ''
      ? Number(strategyData.initial_capital_required)
      : NaN;

  if (!Number.isFinite(roiNum) && !Number.isFinite(capNum)) return null;

  const projectStyle =
    strategyType === STRATEGY_TYPES.FIX_AND_FLIP ||
    strategyType === STRATEGY_TYPES.DEVELOPMENT ||
    strategyType === STRATEGY_TYPES.COMMERCIAL_CONVERSION;

  const roiLabel = projectStyle ? 'Project ROI' : 'Cash-on-cash (Y1)';
  const capLabel = projectStyle ? 'Capital required' : 'Cash to complete';

  const parts = [];
  if (Number.isFinite(roiNum)) parts.push(`${roiLabel}: ${roiNum.toFixed(1)}%`);
  if (Number.isFinite(capNum)) parts.push(`${capLabel}: £${capNum.toLocaleString()}`);

  return (
    <div className="text-[10px] text-gray-500 text-center mt-1 pt-1 border-t border-gray-100/80 shrink-0 leading-snug px-1">
      {parts.join(' · ')}
    </div>
  );
}

/**
 * StrategyChart Component
 * Renders strategy-specific charts based on the selected strategy type
 */
function StrategyChart({ strategyType, strategyData, result }) {
  const config = getStrategyConfig(strategyType);

  // Render different charts based on strategy type
  const renderChart = () => {
    try {
      switch (strategyType) {
        case STRATEGY_TYPES.BUY_TO_LET:
          return <BuyToLetChart result={result} />;
        
        case STRATEGY_TYPES.FIX_AND_FLIP:
          return <FixAndFlipChart strategyData={strategyData} result={result} />;
        
        case STRATEGY_TYPES.HMO_CONVERSION:
          return <HMOComparisonChart result={result} strategyData={strategyData} />;
        
        case STRATEGY_TYPES.SHORT_TERM_LET:
          return <ShortTermLetChart strategyData={strategyData} />;
        
        case STRATEGY_TYPES.DEVELOPMENT:
          return <DevelopmentChart strategyData={strategyData} />;
        
        case STRATEGY_TYPES.COMMERCIAL_CONVERSION:
          return <ConversionChart strategyData={strategyData} />;
        
        case STRATEGY_TYPES.LAND_BANKING:
          return <LandBankingChart strategyData={strategyData} />;
        
        default:
          return <BuyToLetChart result={result} />;
      }
    } catch (error) {
      console.error('Chart rendering error:', error);
      return (
        <div className="flex items-center justify-center h-full text-gray-500 text-sm">
          Error loading chart
        </div>
      );
    }
  };

  return (
    <div className="bg-gray-50 rounded-lg p-3 h-full flex flex-col min-h-0">
      <h4 className="font-bold text-gray-900 mb-2 text-sm truncate">
        {config?.displayName || 'Strategy'} Analysis
      </h4>
      <div className="flex-1 min-h-0 flex flex-col" style={{ minHeight: '180px' }}>
        <div className="flex-1 min-h-0 min-h-[140px]">
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        </div>
        <StrategyRoiCapitalFooter strategyType={strategyType} strategyData={strategyData} />
      </div>
    </div>
  );
}

// Buy-to-Let: Cashflow from analysis (Year 1 actual; optional growth from metrics if available)
function BuyToLetChart({ result }) {
  try {
    const monthlyCashflow = result?.metrics?.monthly_cashflow;
    const totalReturnAnnual = result?.metrics?.total_return_annual;
    
    if (monthlyCashflow == null || monthlyCashflow === undefined) {
      return (
        <div className="flex items-center justify-center h-full text-gray-500 text-sm">
          No cashflow data from analysis
        </div>
      );
    }
    
    const y1 = Number(monthlyCashflow) * 12;
    
    // Safely get chart config
    const analyzedStrategies = result?.strategy_analysis?.analyzed_strategies || [];
    const buyToLetStrategy = analyzedStrategies.find(s => s && s.strategy === STRATEGY_TYPES.BUY_TO_LET);
    const chartConfig = buyToLetStrategy?.chart_config;
    
    const growthRate = chartConfig?.annual_growth_rate 
      ?? (totalReturnAnnual != null ? Number(totalReturnAnnual) / 100 : null)
      ?? DEFAULT_GROWTH_RATE;
    
    const growth = 1 + Number(growthRate);
    const data = [
      { year: 'Year 1', cashflow: y1, label: 'From analysis' },
      { year: 'Year 2', cashflow: y1 * growth, label: 'Projected' },
      { year: 'Year 3', cashflow: y1 * growth * growth, label: 'Projected' },
      { year: 'Year 4', cashflow: y1 * Math.pow(growth, 3), label: 'Projected' },
      { year: 'Year 5', cashflow: y1 * Math.pow(growth, 4), label: 'Projected' },
    ];

    return (
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="year" tick={{ fontSize: 10 }} />
        <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
        <Tooltip formatter={(v) => `£${Number(v).toLocaleString()}`} />
        <Bar dataKey="cashflow" fill="#3b82f6" name="Annual pre-tax cashflow" />
      </BarChart>
    );
  } catch (error) {
    console.error('BuyToLetChart error:', error);
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        Error rendering chart
      </div>
    );
  }
}

// Fix and Flip: Profit waterfall from chart_config + purchase price from snapshot when needed
function FixAndFlipChart({ strategyData, result }) {
  const cc = strategyData?.chart_config || {};
  const arv = strategyData?.arv ?? cc.arv;
  const purchasePrice =
    strategyData?.purchase_price ?? result?.property_snapshot?.financials?.asking_price;
  const renovationCost = strategyData?.renovation_cost ?? cc.renovation_cost ?? 0;
  const holdingCostsMonthly = strategyData?.holding_costs_monthly;
  const timelineMonths = strategyData?.timeline_months ?? cc.holding_months;
  const holdingCostsTotal = cc.holding_costs_total != null ? Number(cc.holding_costs_total) : null;
  const hasCore = (arv != null && arv > 0) || (purchasePrice != null && purchasePrice > 0);
  if (!hasCore) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        No fix & flip data from analysis
      </div>
    );
  }
  const purchase = purchasePrice ?? 0;
  const holdingCosts =
    holdingCostsMonthly != null && timelineMonths != null
      ? holdingCostsMonthly * timelineMonths
      : holdingCostsTotal ?? 0;
  // Use backend chart_config selling costs, or research-backed default (~3%)
  const sellingCostsPercent = cc.selling_costs_percent ?? SELLING_COSTS.TOTAL_PCT;
  const sellingCosts = (arv ?? 0) * sellingCostsPercent;
  const profit = (arv ?? 0) - purchase - renovationCost - holdingCosts - sellingCosts;

  const data = [
    { name: 'Purchase', value: -purchase, color: '#ef4444' },
    { name: 'Renovation', value: -renovationCost, color: '#f97316' },
    { name: 'Holding', value: -holdingCosts, color: '#eab308' },
    { name: 'Selling', value: -sellingCosts, color: '#84cc16' },
    { name: 'Sale (ARV)', value: arv ?? 0, color: '#22c55e' },
    { name: 'Profit', value: profit, color: '#3b82f6' },
  ].filter((d) => d.value !== 0 || d.name === 'Profit');

  return (
    <BarChart data={data} layout="vertical">
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `£${v / 1000}k`} />
      <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={80} />
      <Tooltip formatter={(v) => `£${v.toLocaleString()}`} />
      <Bar dataKey="value">
        {data.map((entry, index) => (
          <Cell key={`cell-${index}`} fill={entry.color} />
        ))}
      </Bar>
    </BarChart>
  );
}

// HMO: Single let vs HMO yield from analysis only
function HMOComparisonChart({ result, strategyData }) {
  const singleYield = result?.metrics?.gross_yield;
  const hmoYield = strategyData?.gross_yield;
  const hasSingle = singleYield != null && typeof singleYield === 'number';
  const hasHmo = hmoYield != null && typeof hmoYield === 'number';
  if (!hasSingle && !hasHmo) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        No yield data from analysis
      </div>
    );
  }
  const data = [
    ...(hasSingle ? [{ name: 'Single Let', yield: singleYield }] : []),
    ...(hasHmo ? [{ name: 'HMO', yield: hmoYield }] : []),
  ];

  return (
    <BarChart data={data}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
      <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
      <Tooltip formatter={(v) => `${v}%`} />
      <Bar dataKey="yield" fill="#8b5cf6" name="Gross Yield %" />
    </BarChart>
  );
}

// Short-term let: Only from analysis (nightly rate & occupancy)
function ShortTermLetChart({ strategyData }) {
  const cc = strategyData?.chart_config || {};
  const baseRate = strategyData?.nightly_rate ?? cc.nightly_rate;
  const occupancy = strategyData?.occupancy_rate ?? cc.occupancy_rate_pct;
  if ((baseRate == null || baseRate === undefined) && (occupancy == null || occupancy === undefined)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        No short-term let data from analysis
      </div>
    );
  }
  // Single summary bar from analysis when we have at least one metric
  const rate = baseRate != null ? Number(baseRate) : null;
  const occ = occupancy != null ? Number(occupancy) : null;
  const data = [
    { month: 'Avg', rate: rate ?? 0, occupancy: occ ?? 0 },
  ];
  if (rate != null && occ != null) {
    // Show one data point from analysis
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2">
        {rate > 0 && <div className="text-lg font-bold text-green-700">£{rate} <span className="text-xs font-normal text-gray-500">nightly</span></div>}
        {occ > 0 && <div className="text-lg font-bold text-blue-700">{occ}% <span className="text-xs font-normal text-gray-500">occupancy</span></div>}
      </div>
    );
  }
  return (
    <AreaChart data={data}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="month" tick={{ fontSize: 9 }} />
      <YAxis yAxisId="left" tick={{ fontSize: 10 }} tickFormatter={(v) => `£${v}`} />
      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
      <Tooltip />
      <Area yAxisId="left" type="monotone" dataKey="rate" stroke="#22c55e" fill="#22c55e" fillOpacity={0.3} name="Nightly Rate" />
      <Line yAxisId="right" type="monotone" dataKey="occupancy" stroke="#3b82f6" strokeWidth={2} name="Occupancy %" />
    </AreaChart>
  );
}

// Development: Timeline and costs from analysis only
function DevelopmentChart({ strategyData }) {
  const cc = strategyData?.chart_config || {};
  const timeline = strategyData?.timeline_months;
  const planningCost = strategyData?.planning_cost ?? cc.planning_cost;
  const buildCost = strategyData?.build_cost ?? cc.build_cost;
  const capitalRequired = strategyData?.initial_capital_required;
  const hasData = timeline != null || planningCost != null || buildCost != null || capitalRequired != null;
  if (!hasData) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        No development data from analysis
      </div>
    );
  }
  const data = [
    ...(timeline != null ? [{ phase: 'Timeline', value: Number(timeline), label: 'months' }] : []),
    ...(capitalRequired != null ? [{ phase: 'Capital required', value: Number(capitalRequired), label: '£' }] : []),
    ...(buildCost != null ? [{ phase: 'Build', value: Number(buildCost), label: '£' }] : []),
    ...(planningCost != null ? [{ phase: 'Planning', value: Number(planningCost), label: '£' }] : []),
  ];

  return (
    <BarChart data={data}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="phase" tick={{ fontSize: 9 }} interval={0} angle={-15} textAnchor="end" height={50} />
      <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `£${v / 1000}k` : v} />
      <Tooltip formatter={(v, name, props) => props.payload?.label === '£' ? `£${Number(v).toLocaleString()}` : `${v} months`} />
      <Bar dataKey="value" fill="#ef4444" name="Value" />
    </BarChart>
  );
}

// Commercial Conversion: Planning risk and timeline from analysis only
function ConversionChart({ strategyData }) {
  const cc = strategyData?.chart_config || {};
  const planningRisk = strategyData?.planning_risk_score ?? cc.planning_risk_score;
  const timeline = strategyData?.timeline_months;
  const capitalRequired = strategyData?.initial_capital_required;
  const estimatedProfit = strategyData?.estimated_profit;
  const hasData = planningRisk != null || timeline != null || capitalRequired != null || estimatedProfit != null;
  if (!hasData) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        No conversion data from analysis
      </div>
    );
  }
  const data = [
    ...(planningRisk != null ? [{ metric: 'Planning risk', value: Number(planningRisk), label: 'score (1-5)' }] : []),
    ...(timeline != null ? [{ metric: 'Timeline', value: Number(timeline), label: 'months' }] : []),
    ...(capitalRequired != null ? [{ metric: 'Capital required', value: Number(capitalRequired), label: '£' }] : []),
    ...(estimatedProfit != null ? [{ metric: 'Est. profit', value: Number(estimatedProfit), label: '£' }] : []),
  ];

  return (
    <BarChart data={data}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="metric" tick={{ fontSize: 9 }} interval={0} angle={-20} textAnchor="end" height={50} />
      <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `£${v / 1000}k` : v} />
      <Tooltip formatter={(v, name, props) => props.payload?.label === '£' ? `£${Number(v).toLocaleString()}` : `${v} ${props.payload?.label || ''}`} />
      <Bar dataKey="value" fill="#6366f1" name="Value" />
    </BarChart>
  );
}

// Land Banking: Only values from analysis (current and/or future value)
function LandBankingChart({ strategyData }) {
  const cc = strategyData?.chart_config || {};
  const currentValue = strategyData?.current_value ?? cc.current_value;
  const futureValue = strategyData?.future_value_estimate ?? cc.future_value_estimate;
  const hasData = currentValue != null || futureValue != null;
  if (!hasData) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        No land banking data from analysis
      </div>
    );
  }
  const data = [
    ...(currentValue != null ? [{ year: 'Current', value: Number(currentValue) }] : []),
    ...(futureValue != null ? [{ year: 'Future (est.)', value: Number(futureValue) }] : []),
  ];

  return (
    <BarChart data={data}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="year" tick={{ fontSize: 10 }} />
      <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `£${v / 1000}k`} />
      <Tooltip formatter={(v) => `£${Number(v).toLocaleString()}`} />
      <Bar dataKey="value" fill="#14b8a6" name="Value" />
    </BarChart>
  );
}

export default StrategyChart;
